import { create } from 'zustand';

import { isApiError } from '../api/errors';
import { api } from '../api/http';
import {
  isTwoFactorChallenge,
  type AuthResponse,
  type CurrentUser,
  type IsoDateTime,
  type LoginResponse,
  type MenuLayout,
  type UpdatePreferencesRequest,
} from '../api/types';

import { broadcastAuth } from './crossTab';
import { getAuthState } from './store';
import { refreshAccessToken, withSessionLock } from './token';

export interface LoginRequest {
  email: string;
  password: string;
  /** false → the refresh cookie lives for the browser session only. */
  rememberMe?: boolean;
}

export interface AcceptInvitationRequest {
  firstName: string;
  lastName: string;
  password: string;
}

/** `GET /api/invitations/{token}` (anonymous): who is invited, by whom, until when (ARCHITECTURE §4.6). */
export interface InvitationPreview {
  email: string;
  firstName: string | null;
  lastName: string | null;
  invitedByName: string | null;
  expiresAt: IsoDateTime;
}

/** Boot state of the session check (refresh cookie → access token). */
interface BootState {
  /** The API could not be reached while restoring the session. */
  failed: boolean;
  setFailed: (failed: boolean) => void;
}

export const useBootStore = create<BootState>()((set) => ({
  failed: false,
  setFailed: (failed) => set({ failed }),
}));

/**
 * Restores the session on startup via the refresh cookie. Resolves when the auth status is known;
 * when the API is unreachable, failing or rate limiting (429) the status stays `unknown` and
 * `useBootStore().failed` is set (UI offers retry) — the refresh cookie may well still be valid.
 */
export async function bootstrapSession(): Promise<void> {
  useBootStore.getState().setFailed(false);
  try {
    const token = await refreshAccessToken();
    if (!token && getAuthState().status === 'unknown') getAuthState().clear();
  } catch (error) {
    if (isApiError(error) && (error.isNetworkError || error.status >= 500 || error.status === 429)) {
      useBootStore.getState().setFailed(true);
      return;
    }
    getAuthState().clear();
  }
}

/**
 * Thrown by {@link login} when the password was right but the account has two-factor authentication: nothing is signed in
 * yet. `token` is the pending second step (a few minutes, this browser only, once) that {@link loginWithTwoFactor} needs.
 */
export class TwoFactorRequiredError extends Error {
  override readonly name = 'TwoFactorRequiredError';
  readonly token: string;
  readonly expiresAt: IsoDateTime;

  constructor(token: string, expiresAt: IsoDateTime) {
    super('Two-factor authentication is required.');
    this.token = token;
    this.expiresAt = expiresAt;
  }
}

/** Signs in with e-mail and password; rejects with {@link TwoFactorRequiredError} when a code is needed next. */
export async function login(request: LoginRequest): Promise<CurrentUser> {
  const response = await api.post<LoginResponse>('/auth/login', request, { auth: false });
  if (isTwoFactorChallenge(response)) throw new TwoFactorRequiredError(response.twoFactorToken, response.expiresAt);
  getAuthState().setSession(response);
  broadcastAuth({ type: 'login' });
  return response.user;
}

/**
 * The second step of a sign-in: a code of the authenticator app (6 digits) or a recovery code, with the token
 * {@link login} rejected with. Wrong codes count towards the account's lockout.
 */
export async function loginWithTwoFactor(token: string, code: string): Promise<CurrentUser> {
  const auth = await api.post<AuthResponse>('/auth/login/two-factor', { twoFactorToken: token, code }, { auth: false });
  getAuthState().setSession(auth);
  broadcastAuth({ type: 'login' });
  return auth.user;
}

/** Loads the invitation behind a link; 404 (unknown, used or revoked) and 410 (expired) mean it is no longer valid. */
export function fetchInvitationPreview(token: string, signal?: AbortSignal): Promise<InvitationPreview> {
  return api.get<InvitationPreview>(`/invitations/${encodeURIComponent(token)}`, { auth: false, signal });
}

export async function acceptInvitation(
  token: string,
  request: AcceptInvitationRequest,
): Promise<CurrentUser> {
  const auth = await api.post<AuthResponse>(`/invitations/${encodeURIComponent(token)}/accept`, request, {
    auth: false,
  });
  getAuthState().setSession(auth);
  broadcastAuth({ type: 'login' });
  return auth.user;
}

/** How long signing out waits for the "before sign-out" work of the modules (a slow server must not keep the user signed in). */
export const BEFORE_SIGN_OUT_TIMEOUT_MS = 3000;

const beforeSignOut = new Set<() => Promise<void>>();

/**
 * Registers work that must run while the session is still valid, when the user signs out (a module un-registering this
 * device's push notifications, say — after the sign-out the API would refuse it). Failures and a slow server are ignored:
 * they never keep the user signed in. Returns the function that removes the handler.
 */
export function onBeforeSignOut(handler: () => Promise<void>): () => void {
  beforeSignOut.add(handler);
  return () => {
    beforeSignOut.delete(handler);
  };
}

/** Runs the registered work, waiting for it at most `timeoutMs`. */
export async function runBeforeSignOut(timeoutMs: number = BEFORE_SIGN_OUT_TIMEOUT_MS): Promise<void> {
  if (beforeSignOut.size === 0) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, timeoutMs);
  });
  const work = Promise.allSettled([...beforeSignOut].map((handler) => handler())).then(() => undefined);
  try {
    await Promise.race([work, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Signs out: the server revokes the session and deletes the HttpOnly refresh cookie (only it can), then every tab
 * clears its session. Runs under the session lock, so no refresh of this or another tab rotates the cookie meanwhile.
 *
 * If the request fails (offline, 5xx, 429) the user stays signed in and the promise rejects with the `ApiError`:
 * clearing only the screen would leave a valid cookie behind, and the next person to open the portal on a shared
 * computer would be signed in as this user. 401/403 mean the server has no session any more — signed out.
 */
export async function logout(): Promise<void> {
  await runBeforeSignOut();
  await withSessionLock(async () => {
    try {
      await api.post('/auth/logout', undefined, { auth: false });
    } catch (error) {
      if (!isApiError(error) || (error.status !== 401 && error.status !== 403)) throw error;
    }
  });
  getAuthState().clear('logout');
  broadcastAuth({ type: 'logout' });
}

export async function fetchCurrentUser(): Promise<CurrentUser> {
  const user = await api.get<CurrentUser>('/auth/me');
  getAuthState().setUser(user);
  return user;
}

/** Persists UI preferences on the server; the response (CurrentUser or 204) updates the local user. */
export async function updatePreferences(request: UpdatePreferencesRequest): Promise<void> {
  getAuthState().patchUser(request);
  const result = await api.put<CurrentUser | undefined>('/auth/me/preferences', request);
  if (result && typeof result === 'object' && 'id' in result) getAuthState().setUser(result);
}

/** Saves of the menu go to the server one after another, so the last arrangement the person made is the one that stays. */
let menuSaves: Promise<unknown> = Promise.resolve();

/**
 * Saves the person's own arrangement of the left menu (`null`: back to the portal's default). The menu changes at once; when the
 * server refuses, the previous arrangement comes back and the error is thrown.
 */
export function saveMenu(layout: MenuLayout | null): Promise<void> {
  const previous = getAuthState().user?.menu ?? null;
  getAuthState().patchUser({ menu: layout });
  const run = async (): Promise<void> => {
    try {
      if (layout) await api.put('/auth/me/menu', layout);
      else await api.delete('/auth/me/menu');
    } catch (error) {
      getAuthState().patchUser({ menu: previous });
      throw error;
    }
  };
  const saved = menuSaves.then(run, run);
  menuSaves = saved.catch(() => undefined);
  return saved;
}
